import React, { useState, useEffect } from 'react'
import Icon from 'components/Icon'
import { Button, Input, TextArea, Field, FileIcon, Checkbox } from '@arnsbogroup/react-voicearchive'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/hooks'
import useValidate, { is } from '@arnsbogroup/react-voicearchive/hooks/useValidate'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import products from 'data/products/list'
import productNames from 'data/products/messages'
import { Select, Card } from '@arnsbogroup/react-voicearchive'
import ProjectServices from '../ProjectServices'
import FileUpload from 'components/EditableImg'
import mediums from 'data/mediums/list'
import mediumNames from 'data/mediums/messages'

const ProjectDetails = ({ dispatch, project, onChange, onSubmit: _onSubmit, loading, onCancel }) => {
  const [originalProjectDetails, setOriginalProjectDetails] = useState({})

  useEffect(() => {
    if (project.id && !Object.keys(originalProjectDetails).length) {
      setOriginalProjectDetails(project.details)
    }

    if (project?.details?.subscribeToNewsletter == undefined) {
      onChange('details', 'subscribeToNewsletter')({
        target: { value: true }
      })
    }

  }, [project.id, project.details])

  const [validations, validate] = useValidate(project.details, {
    name: [is.present],
    email: [is.present, is.email],
    companyName: [is.present],
    brief: [is.present],
  })

  const onSubmit = (event) => {
    if (validate()) _onSubmit(event)
  }

  useKeyboardShortcut(['Escape'], onCancel, { overrideSystem: true })
  useKeyboardShortcut(['Meta', 'Enter'], onSubmit)

  const { formatMessage } = useIntl()
  const productOptions = products.map(({ key }) => ({
    key,
    label: formatMessage(productNames[key]),
    value: key,
  }))

  const mediaOptions = mediums.map((item) => ({
    value: item.id,
    label: mediumNames[item.key] ? formatMessage(mediumNames[item.key]) : item.key,
  })).sort((x, y) => {
    let a = x.label.toUpperCase(),
      b = y.label.toUpperCase()
    return a == b ? 0 : a > b ? 1 : -1
  })

  const i18n = (messages) => messages?.map((message) => ({
    present: `Must be filled out`,
    email: `E-mail must be a valid e-mail like 'name@company.org'`,
    set: `Must be filled out`,
  }[message] || message))

  return (
    <div className="space-y-10 py-8">
      <h1 className="text-base text-gray-700 font-medium text-sm space-x-2">
        <span>1. Now we need some details on your project</span>
      </h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mx-6 sm:mx-0">
        <div className="space-y-4">
          <Input
            autoFocus
            label="Name"
            placeholder="Your full name"
            value={project.details.name}
            onChange={onChange('details', 'name')}
            onBlur={() => validate(['name'])}
            errors={i18n(validations.name)}
          />
          <Input
            label="E-mail"
            type="email"
            placeholder="Work e-mail address"
            value={project.details.email}
            onChange={onChange('details', 'email')}
            onBlur={() => validate(['email'])}
            errors={i18n(validations.email)}
          />
          <Input
            label="Company"
            placeholder="Company or organisation name"
            value={project.details.companyName}
            onChange={onChange('details', 'companyName')}
            onBlur={() => validate(['companyName'])}
            errors={i18n(validations.companyName)}
          />
        </div>
        <div className="space-y-4">
          <TextArea
            label="Brief"
            placeholder="Share as much information about your project as possible. I.e. use, end-client, production plan, etc."
            rows={5}
            value={project.details.brief}
            onChange={onChange('details', 'brief')}
            onBlur={() => validate(['brief'])}
            errors={i18n(validations.brief)}
          />

          <Field
            optional
            label="Script"
            element={() => (
              <>
                <FileUpload
                  onChange={(url, { name, size, type, lastModified }) => {
                    dispatch({ type: 'PATCH', payload: {
                      ...project,
                      files: [
                        ...(project.files || []),
                        {
                          url,
                          name,
                          size,
                          type,
                          lastModified,
                        }
                      ],
                    }})
                  }}
                >
                  <div className="flex item-center justify-center p-4">
                    <span className="text-gray-400 text-sm">Upload a script or reference file here <Icon icon="file-upload" /></span>
                  </div>
                </FileUpload>

                { (project.files || []).map((file) => (
                  <Card key={file.name} className="p-2 flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <FileIcon filename={file.name} className="text-gray-600 text-lg" />
                      <div>
                        <h1 className="text-sm font-medium">{ file.name }</h1>
                        <h3 className="text-xs text-gray-600">{ (file.size / (1024*1024)).toFixed(2) } MB</h3>
                      </div>
                    </div>
                    <button
                      className="text-sm text-gray-600 hover:text-negative"
                      onClick={() => {
                        dispatch({
                          type: 'PATCH',
                          payload: {
                            ...project,
                            files: project.files.filter(({ url }) => url !== file.url)
                          }
                        })
                      }}
                    >
                      Remove
                    </button>
                  </Card>
                )) }
              </>
            )}
          />
        </div>
        <div className="space-y-4">
          <Field
            optional
            label={formatMessage(messages.productType)}
            element={() =>
              <Select
                className="bg-white"
                styles={{
                  menu: (styles) => ({
                    ...styles,
                    width: 'max-content',
                    minWidth: '100%',
                  }),
                }}
                options={productOptions}
                onChange={({ value }) =>
                  onChange('details', 'productType')({ target: { value } })
                }
                value={
                  productOptions.find(
                    ({ key }) => key == project.details.productType
                  ) || null
                }
                placeholder={formatMessage(messages.productType)}
              />
            }
          />


          { ['commercial', 'video-presentation', 'character'].includes(project.details.productType) &&
            <Field
              optional
              label="Media"
              element={() =>
                <Select
                  className="inline-block w-auto relative text-sm md:text-base bg-white"
                  label="Media"
                  options={mediaOptions}
                  onChange={(opts) => dispatch({
                    type: 'SET_MEDIA_IDS',
                    payload: opts.map(({ value }) => value)
                  })}
                  value={mediaOptions.filter(({ value }) => {
                    return project.mediaIds.includes(value)
                  }) || null}
                  placeholder="Media"
                  isMulti
                />
              }
            />
          }


          <Input
            optional
            type="number"
            min={0}
            label={
              ['commercial', 'video-presentation', 'character']
                .includes(project.details.productType) ?
                'Expeted months of usage' :
                'Estimated word count in script (per language)'
            }
            value={project.details.productQuantity}
            onChange={onChange('details', 'productQuantity')}
            onBlur={() => validate(['productQuantity'])}
            errors={i18n(validations.productQuantity)}
          />

            <Checkbox
              onChange={({ target: { checked } }) => {
                onChange('details', 'subscribeToNewsletter')({
                  target: { value: checked }
                })
              }}
              checked={
                project.details?.subscribeToNewsletter === undefined
                ? true
                : project.details?.subscribeToNewsletter
              }
              label={formatMessage(messages.subscribeToNewsletter)}
            />
        </div>
      </div>

      { project.details.productType &&
        <ProjectServices project={project} dispatch={dispatch} />
      }

      <div className="space-x-2 flex justify-end">
        <Button state="tertiary" variant="outline" onClick={onCancel}>
          <FormattedMessage {...messages.cancel} />
        </Button>

        { loading ? (
          <Button state="tertiary" disabled>
            <Icon icon="spinner-third" className="animate-spin mr-1" />
            <FormattedMessage {...messages.saving} />
          </Button>
        ) : (
          <Button
            state="tertiary"
            onClick={onSubmit}
            disabled={Object.keys(project.details || {}).every((key) => project.details[key] == originalProjectDetails[key])}
          >
            { Object.values(originalProjectDetails).every((v) => !v) ? (
              <FormattedMessage {...messages.save} />
            ) : (
              <FormattedMessage {...messages.saveChanges} />
            ) }
          </Button>
        )}
      </div>
    </div>
  )
}

export default ProjectDetails
